/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useReducer } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { AxiosResponse } from 'axios';
import { useDispatch, useSelector } from 'react-redux';

import { usePath, useDeepEffect } from '~/hooks';
import { selectPageShow, showClose, showOpen } from '~/slices/pageShow';
import onError from '~/errorsHandler';

export type QueryData<T = any> = {
  data: T;
  total: number;
};

type UseQueryType = typeof useQuery;
type UseQueryParams = Parameters<UseQueryType>;

type UseMutationType = typeof useMutation;
type UseMutationParams = Parameters<UseMutationType>;

export const usePageQuery = <T = any>(
  key: UseQueryParams[0] | string,
  req: (params: ReturnType<typeof getHookQueryParams>) => Promise<AxiosResponse>,
  options: UseQueryParams[2] = {}
) => {
  const { params: pathParamsRaw } = usePath();
  const params = getHookQueryParams(pathParamsRaw);
  const { page, perPage, ...restParams } = params;

  const queryClient = useQueryClient();
  const resultKey = typeof key === 'string' ? [key, page] : key;

  const queryFn = () => req(params).then(handleAxiosResponse);
  const { data, ...rest } = useQuery(resultKey, queryFn, {
    keepPreviousData: true,
    enabled: !!pathParamsRaw.perPage,
    ...(options as any),
  });

  const [, dispatch] = useReducer(
    (state: any, action: { type: any }) => {
      switch (action.type) {
        case 'prefetch': {
          const hasMore = page * perPage < (data?.total || 0);
          if (typeof key === 'string' && hasMore && !isEmpty(data?.data) && pathParamsRaw.perPage) {
            const nextPage: number = (page as number) + 1;
            void queryClient.prefetchQuery([key, nextPage], () =>
              req({ ...params, page: nextPage }).then(handleAxiosResponse)
            );
          }
          return { mounted: true };
        }
        case 'refetch': {
          if (pathParamsRaw.perPage && options?.enabled !== false && state.mounted) {
            void queryClient.prefetchQuery(resultKey, () =>
              req({ ...params }).then(handleAxiosResponse)
            );
          }
          return { mounted: true };
        }
        default:
          return null;
      }
    },
    { mounted: false }
  );

  useDeepEffect(() => {
    dispatch({ type: 'prefetch' });
  }, [dispatch, params, data]);

  useDeepEffect(() => {
    dispatch({ type: 'refetch' });
  }, [dispatch, params.perPage, restParams]);

  return { ...(data as QueryData<T>), ...rest };
};

export const useEditMutation = <T extends any[] = any>(
  key: UseQueryParams[0] | string,
  req: (params: any) => Promise<AxiosResponse>,
  options?: UseMutationParams[2]
) => {
  const queryClient = useQueryClient();
  const { params: pathParamsRaw } = usePath();
  const params = getHookQueryParams(pathParamsRaw);
  const resultKey = typeof key === 'string' ? [key, params.page] : key;
  const [t] = useTranslation();

  return useMutation(req, {
    onMutate: (variables: any) => {
      if (options?.onMutate) options.onMutate(variables);

      const prevData = queryClient.getQueryData<QueryData>(resultKey);
      const rollback = () => {
        queryClient.setQueryData(resultKey, prevData);
      };
      queryClient.setQueryData<QueryData<T> | undefined>(resultKey, (prev: any) => {
        if (prev) {
          return {
            ...prev,
            data: prev?.data.map((item: any) => {
              if (item.id === variables.id && variables.body) return variables.body;
              return item;
            }),
          };
        }
        return prev;
      });

      void message.loading({ content: t('updating'), key });
      const successMessage = () => {
        void message.success({ content: t('updated'), key, duration: 2 });
      };
      return { successMessage, rollback };
    },
    // Notice the second argument is the variables object that the `mutate` function receives
    onSuccess: (newData, variables, onMutateReturn) => {
      if (options?.onSuccess) void options.onSuccess(newData, variables, onMutateReturn);
      if (onMutateReturn?.successMessage) void onMutateReturn.successMessage();
    },
    onError: (error: any, editableValue, onMutateReturn) => {
      if (onMutateReturn?.rollback) onMutateReturn.rollback();
      onError(error);
    },
  });
};

export const useCreateMutation = (
  key: UseMutationParams[0] | string,
  req: (params: any) => Promise<AxiosResponse>,
  options?: UseMutationParams[2]
) => {
  const queryClient = useQueryClient();
  const { params } = usePath();
  const { page, perPage } = params;
  const resultKey = typeof key === 'string' ? [key, params.page] : key;
  const [t] = useTranslation();

  return useMutation(req, {
    onMutate: (variables: any) => {
      if (options?.onMutate) options.onMutate(variables);
      const nextPageKey = [key, ({ ...params }.page as number) + 1];
      const currentPageData = queryClient.getQueryData<QueryData | undefined>(resultKey);
      const nextPageDataCopy = queryClient.getQueryData<QueryData<any>>(nextPageKey);
      const rollback = () => {
        queryClient.setQueryData(resultKey, currentPageData);
        queryClient.setQueryData(nextPageKey, nextPageDataCopy);
      };

      const total: number = currentPageData?.total || 0;
      const isLastPage = total - page * perPage <= 0;
      const hasFreeSpaceOnPage = page * perPage - total > 0;

      queryClient.setQueryData<QueryData | undefined>(resultKey, (prev) => {
        if (prev) {
          if (isLastPage && hasFreeSpaceOnPage) {
            return {
              ...prev,
              total: total + 1,
              data: (prev.data || []).concat([variables]),
            };
          }

          return {
            ...prev,
            total: total + 1,
          };
        }
        return prev;
      });

      queryClient.setQueryData<QueryData | undefined>(nextPageKey, (nextPageData) => {
        if (nextPageData) {
          const hasFreeSpaceOnNextPage = ((page as number) + 1) * perPage - total > 0;
          if (hasFreeSpaceOnNextPage) {
            return {
              ...nextPageData,
              data: (nextPageData.data || []).concat([variables]),
              total: total + 1,
            };
          }
        }
        return {
          data: [variables],
          total: total + 1,
        };
      });

      const refetchNextPage = () => {
        void queryClient.refetchQueries(nextPageKey);
      };

      void message.loading({ content: t('creating'), key });
      const successMessage = () => {
        void message.success({ content: t('created'), key, duration: 2 });
      };
      return { successMessage, refetchNextPage, rollback };
    },
    // Notice the second argument is the variables object that the `mutate` function receives
    onSuccess: (newData, variables, onMutateReturn) => {
      if (options?.onSuccess) void options.onSuccess(newData, variables, onMutateReturn);
      void queryClient.refetchQueries(resultKey);
      if (onMutateReturn?.successMessage) void onMutateReturn.successMessage();
      if (onMutateReturn?.refetchNextPage) void onMutateReturn.refetchNextPage();
    },
    onError: (error: any, editableValue, onMutateReturn) => {
      if (onMutateReturn?.rollback) onMutateReturn.rollback();
      onError(error);
    },
  });
};

export const useDeleteMutation = (
  key: UseMutationParams[0] | string,
  req: (params: any) => Promise<AxiosResponse>,
  options?: UseMutationParams[2]
) => {
  const queryClient = useQueryClient();
  const { params, changePath } = usePath();
  const resultKey = typeof key === 'string' ? [key, params.page] : key;
  const [t] = useTranslation();

  return useMutation(req, {
    onMutate: (variables: any) => {
      if (options?.onMutate) options.onMutate(variables);

      const nextPageKey = [key, (params.page as number) + 1];
      const prevData = queryClient.getQueryData<QueryData>(resultKey);
      const nextPageData = queryClient.getQueryData<QueryData<any>>(nextPageKey);

      const rollback = () => {
        queryClient.setQueryData(resultKey, prevData);
        queryClient.setQueryData(nextPageKey, nextPageData);
      };

      queryClient.setQueryData(resultKey, (prev: any) => {
        if (prev) {
          const total: number = prev.total || 0;
          const filteredData = prev.data.filter((item: any) => item.id !== variables.id);
          if (prev.data.length === 1 && params.page && params.page > 1) {
            setTimeout(() => {
              changePath({ page: params.page - 1 });
            }, 0);
          }
          if (typeof key === 'string') {
            if (!isEmpty(nextPageData?.data)) {
              const nextPageFirstItem = [nextPageData?.data[0]] || [];
              return {
                ...prev,
                total: total - 1,
                data: filteredData.concat(nextPageFirstItem),
              };
            }
          }
          return {
            ...prev,
            total: total - 1,
            data: filteredData,
          };
        }
        return prev;
      });

      void message.loading({ content: t('deleting'), key });
      const successMessage = () => {
        void message.success({ content: t('deleted'), key, duration: 2 });
      };
      return { successMessage, rollback };
    },
    // Notice the second argument is the variables object that the `mutate` function receives
    onSuccess: (newData, variables, onMutateReturn) => {
      if (options?.onSuccess) void options.onSuccess(newData, variables, onMutateReturn);
      void queryClient.refetchQueries(resultKey);
      if (onMutateReturn?.successMessage) void onMutateReturn.successMessage();
    },
    onError: (error: any, editableValue, onMutateReturn) => {
      if (onMutateReturn?.rollback) onMutateReturn.rollback();
      onError(error);
    },
  });
};

type UsePageShowType = <T = any>(
  pageName: string
) => {
  showOpen: (id: string) => {
    payload: {
      pageName: string;
      id: string;
    };
    type: string;
  };
  showClose: () => {
    payload: {
      pageName: string;
    };
    type: string;
  };
  data: T | null;
  open: boolean;
  id: string | undefined;
};

export const usePageShow: UsePageShowType = <T = any>(pageName: string) => {
  const dispatch = useDispatch();
  const show = useSelector(selectPageShow(pageName));

  const { params } = usePath();
  const client = useQueryClient();
  const queryData = client.getQueryData<QueryData<T[]>>([pageName, params.page])?.data;
  const queryDataAll = client.getQueryData<T[] | QueryData<T[]>>(pageName);

  const dataFromPaginated = getQueryDataArr(queryData)?.find((item) => item.id === show.id) || null;
  const dataFromAll = !dataFromPaginated
    ? getQueryDataArr(queryDataAll)?.find((item) => item.id === show.id)
    : null;
  const data = dataFromPaginated || dataFromAll;

  return {
    ...show,
    data,
    showOpen: (id: string) => dispatch(showOpen({ pageName, id })),
    showClose: () => dispatch(showClose({ pageName })),
  };
};

const getQueryDataArr = (queryData?: QueryData<any[]> | any[]): any[] => {
  if (isEmpty(queryData)) return [];
  if (Array.isArray(queryData)) return queryData;
  if (queryData?.total && queryData.data && Array.isArray(queryData.data)) return queryData.data;
  return [];
};

const getHookQueryParams = (
  params: Record<string, any>,
  queryParams: Record<string, any> = {}
): Record<string, any> => {
  const { page = 1, perPage = usePath.defaultPageSize, filter = {} } = params;
  return { page: queryParams.page || page, perPage: queryParams.perPage || perPage, ...filter };
};

const handleAxiosResponse = <T = any>(res: AxiosResponse<T>): QueryData<T> => {
  return {
    data: res.data,
    total: parseInt(res.headers['content-range']?.split('/').pop(), 10),
  };
};
